<script setup lang="ts">
import { computed } from "vue";

import { useBrandStore } from "@/stores/brand-store";

import StringTooltip from "@/components/Tooltip/StringTooltip.vue";

import type { ScorePointData } from "@/models/Point";

withDefaults(defineProps<{ score: ScorePointData }>(), {  })
defineOptions({ inheritAttrs: false });

const showVerifiedColumn = computed(() => !(useBrandStore().viewerConfig.checks?.hideVerifiedColumn ?? false));

const scorePercentage = computed(() =>
  __props.score.minPc === __props.score.maxPc
    ? NaN
    : Math.round(((__props.score.includedAIScore - __props.score.minPc) / (__props.score.maxPc - __props.score.minPc)) * 100),
);

const verifiedPercentage = computed(() =>
  __props.score.minPc === __props.score.maxPc
    ? NaN
    : Math.round(((__props.score.includedUserVerifiedScore - __props.score.minPc) / (__props.score.maxPc - __props.score.minPc)) * 100),
);

const scoreColor = computed(() => {
  const red = "tw-text-red-600";
  const amber = "tw-text-[#facc15]";
  const green = "tw-text-green-600";
  const black = "tw-text-black";

  switch (__props.score.scoreConnotation) {
    case "negative":
      return { high: red, medium: amber, low: green };
    case "positive":
      return { high: green, medium: amber, low: red };
    default:
      return { high: black, medium: black, low: black };
  }
});
</script>
<template>
  <td aria-hidden="true">
    <div class="tw-grid tw-place-items-center">
      <i
        aria-hidden="true"
        class="tw-w-4 tw-text-center"
        :class="{
          'fa-regular fa-folder-closed': score.scope === 'case',
          'fa-solid fa-timeline': score.scope === 'instance',
          'fa-regular fa-file': score.scope === 'record',
        }"
      />
    </div>
  </td>
  <StringTooltip :item="score.longDescription" class="tw-max-w-sm tw-select-none">
    <td class="!tw-py-2">
      {{ score.shortDescription }}
    </td>
  </StringTooltip>
  <td>
    <div v-if="!isNaN(scorePercentage)" class="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-2">
      <i
        class="fas fa-diamond"
        :class="
          scorePercentage < 100 / 3 ? scoreColor.low : scorePercentage < 200 / 3 ? scoreColor.medium : scoreColor.high
        "
      />{{ scorePercentage }}%
    </div>
  </td>
  <td v-if="showVerifiedColumn">
    <div v-if="!isNaN(verifiedPercentage)" class="tw-grid tw-place-items-center">{{ verifiedPercentage }}%</div>
  </td>
</template>
